import type { ProductEventDataList } from 'corgi-types';

import { ProductEvent } from '../../../enums/Tracking';
import { dispatchTrackingEventGA4 } from '../../../tracking/dispatchTrackingEventGA4';
import { getProductClickEventData } from '../../../tracking/productEventData';
import type { ArticleTracking } from '../../../types/Article';
import { Element } from '../../../types/tracking';

interface GetProductSuggestionTrackingProps {
  tracking: Pick<ArticleTracking, 'productName' | 'productPrice' | 'productSimpleSKU'>;
  list: ProductEventDataList;
  index: number;
}

const event = 'search_click';
const home24ShopName = 'home24';

const enum Placement {
  GlobalHeader = 'global-header',
  ShopInShopNav = 'shop-in-shop-navigation',
}

const enum Label {
  Submit = 'search-submit',
  Back = 'search-back-button',
  Clear = 'search-clear-button',
}

const enum SuggestionType {
  Keyword = 'Keyword',
  RecentSearch = 'Recent Searches',
}

export const getProductSuggestionTrackingData = ({
  tracking,
  list,
  index,
}: GetProductSuggestionTrackingProps) => ({
  event: ProductEvent.Click,
  item_list_name: list,
  items: [getProductClickEventData({ position: index, tracking, list })],
});

export const trackSearchOpen = (shopName?: string) => {
  dispatchTrackingEventGA4({
    event,
    element: Element.Input,
    placement: shopName ? Placement.ShopInShopNav : Placement.GlobalHeader,
    shop_name: shopName ?? home24ShopName,
  });
};

export const trackSearchSubmit = (inputValue: string, shopName?: string) => {
  dispatchTrackingEventGA4({
    event,
    element: Element.Button,
    label: Label.Submit,
    search_term: inputValue,
    placement: shopName ? Placement.ShopInShopNav : Placement.GlobalHeader,
    shop_name: shopName ?? home24ShopName,
  });
};

export const trackSearchBack = (inputValue: string, shopName?: string) => {
  dispatchTrackingEventGA4({
    event,
    element: Element.Button,
    label: Label.Back,
    search_term: inputValue,
    placement: shopName ? Placement.ShopInShopNav : Placement.GlobalHeader,
    shop_name: shopName ?? home24ShopName,
  });
};

export const trackSearchClear = (inputValue: string, shopName?: string) => {
  dispatchTrackingEventGA4({
    event,
    element: Element.Button,
    label: Label.Clear,
    search_term: inputValue,
    placement: shopName ? Placement.ShopInShopNav : Placement.GlobalHeader,
    shop_name: shopName ?? home24ShopName,
  });
};

interface TrackSuggestionClickOptions {
  label: string;
  position: number;
  length: number;
  inputValue?: string;
  shopName?: string;
  isRecentSearch?: boolean;
}

export const trackSuggestionClick = ({
  label,
  position,
  length,
  inputValue = '',
  shopName,
  isRecentSearch = false,
}: TrackSuggestionClickOptions) => {
  dispatchTrackingEventGA4({
    event,
    element: Element.Link,
    link_text: label,
    search_term: inputValue,
    list_position: position,
    list_length: length,
    suggestion_type: isRecentSearch ? SuggestionType.RecentSearch : SuggestionType.Keyword,
    placement: shopName ? Placement.ShopInShopNav : Placement.GlobalHeader,
    shop_name: shopName ?? home24ShopName,
  });
};

interface TrackRemoveRecentSearchOptions {
  label: string;
  position: number;
  length: number;
}

export const trackEditRecentSearches = (label: string) => {
  dispatchTrackingEventGA4({
    event,
    element: Element.Button,
    button_text: label,
    suggestion_type: SuggestionType.RecentSearch,
    placement: Placement.GlobalHeader,
    shop_name: home24ShopName,
  });
};

export const trackRemoveRecentSearch = ({
  label,
  position,
  length,
}: TrackRemoveRecentSearchOptions) => {
  dispatchTrackingEventGA4({
    event,
    element: Element.Icon,
    list_position: position,
    list_length: length,
    list_items: [label],
    suggestion_type: SuggestionType.RecentSearch,
    placement: Placement.GlobalHeader,
    shop_name: home24ShopName,
  });
};

interface TrackRemoveAllRecentSearchOptions {
  label: string;
  items: string[];
  length: number;
}

export const trackRemoveAllRecentSearches = ({
  label,
  items,
  length,
}: TrackRemoveAllRecentSearchOptions) => {
  dispatchTrackingEventGA4({
    event,
    element: Element.Button,
    button_text: label,
    list_length: length,
    list_items: items,
    suggestion_type: SuggestionType.RecentSearch,
    placement: Placement.GlobalHeader,
    shop_name: home24ShopName,
  });
};
